import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { SkillToolKey } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { type KeyboardEvent, useId, useState } from 'react';

interface ToolConfigEditorProps {
  toolKey: SkillToolKey;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

export function ToolConfigEditor({ toolKey, config, onChange }: ToolConfigEditorProps) {
  switch (toolKey) {
    case 'db.list_tables':
    case 'db.describe_table':
      return <DbConfigEditor config={config} onChange={onChange} withMaxRows={false} />;
    case 'db.run_query':
      return <DbConfigEditor config={config} onChange={onChange} withMaxRows />;
    case 'excel.list_my_files':
      return <p className="text-xs text-foreground-muted">추가 설정 없음.</p>;
    case 'excel.describe':
    case 'excel.head':
    case 'excel.query':
      return (
        <MaxRowsEditor config={config} onChange={onChange} defaultValue={200} min={1} max={1000} />
      );
    case 'web.fetch_url':
      return <WebFetchUrlEditor config={config} onChange={onChange} />;
    case 'web.search_grounded':
      return <WebSearchGroundedEditor config={config} onChange={onChange} />;
    case 'http.list_endpoints':
    case 'http.request':
      return <HttpToolConfigEditor config={config} onChange={onChange} />;
    case 'analysis.verify_tag':
      return (
        <AnalysisToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/verification_stage1"
          logPathDefault="/ops-api/api/monitoring/log"
          withSearchYn
        />
      );
    case 'analysis.verify_detail':
      return (
        <AnalysisToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/verification_stage2"
        />
      );
    case 'analysis.apply_task':
      return (
        <AnalysisToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/streaming"
        />
      );
    case 'analysis.get_data':
      return (
        <AnalysisToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/streaming"
        />
      );
    case 'datasearch.split':
      return (
        <DatasearchToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/split"
          withTags
        />
      );
    case 'datasearch.analyze':
      return (
        <DatasearchToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/analyze"
        />
      );
    case 'datasearch.expand':
      return (
        <DatasearchToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/expand"
        />
      );
    case 'datasearch.total_insight':
      return (
        <DatasearchToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/total_insight"
        />
      );
    case 'datasearch.grounding':
      return (
        <DatasearchToolEditor
          config={config}
          onChange={onChange}
          defaultPath="/agent-api/api/agent/grounding"
        />
      );
    case 'email.send':
      return <EmailSendEditor config={config} onChange={onChange} />;
    case 'user.get_attributes':
      return <UserGetAttributesEditor config={config} onChange={onChange} />;
    case 'skill.call':
      return <SkillCallConfigEditor config={config} onChange={onChange} />;
    case 'prompt.run':
      return <PromptRunConfigEditor config={config} onChange={onChange} />;
  }
}

function PromptRunConfigEditor({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const treeQ = useQuery({
    queryKey: ['admin', 'response-prompts', 'tree'],
    queryFn: () => api.responsePromptsTree(),
  });

  const rawSlugs = config.allowedPromptSlugs;
  const selectedSlugs = Array.isArray(rawSlugs)
    ? (rawSlugs.filter((v) => typeof v === 'string') as string[])
    : [];
  const selectedSet = new Set(selectedSlugs);

  const toggle = (slug: string) => {
    const next = selectedSet.has(slug)
      ? selectedSlugs.filter((v) => v !== slug)
      : [...selectedSlugs, slug];
    onChange({ ...config, allowedPromptSlugs: next });
  };

  // 트리에서 file 노드만 평탄화. folder/null slug 제외.
  type Node = {
    id: string;
    type: 'folder' | 'file';
    slug: string | null;
    name: string;
    children: Node[];
  };
  const files: Array<{ slug: string; name: string }> = [];
  const walk = (nodes: Node[]) => {
    for (const n of nodes) {
      if (n.type === 'file' && n.slug) files.push({ slug: n.slug, name: n.name });
      if (n.children.length > 0) walk(n.children);
    }
  };
  if (treeQ.data) walk(treeQ.data.tree as unknown as Node[]);
  files.sort((a, b) => a.slug.localeCompare(b.slug));

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs font-medium">호출 허용 프롬프트 slug</span>
        <span className="text-[11px] text-foreground-muted">
          {selectedSlugs.length === 0 ? '최소 1개 선택' : `${selectedSlugs.length}개 선택`}
        </span>
      </div>
      {treeQ.isLoading ? (
        <p className="text-xs text-foreground-muted">프롬프트 목록 로딩 중…</p>
      ) : files.length > 0 ? (
        <div className="space-y-1 rounded-md border border-border bg-surface px-2 py-1.5 max-h-44 overflow-auto">
          {files.map((f) => (
            <label
              key={f.slug}
              className="flex items-center gap-2 text-xs py-1 px-1 rounded hover:bg-surface-muted/40 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(f.slug)}
                onChange={() => toggle(f.slug)}
                className="size-3.5 accent-accent"
              />
              <span className="font-mono text-[11px]">{f.slug}</span>
              <span className="text-foreground-muted">— {f.name}</span>
            </label>
          ))}
        </div>
      ) : (
        <p className="text-xs text-foreground-muted">
          등록된 프롬프트 file 이 없습니다. 먼저 /admin/response-controls/prompts 에서 추가하세요.
        </p>
      )}
      <p className="mt-1 text-[11px] text-foreground-muted">
        LLM 이 prompt.run 으로 호출 가능한 slug 화이트리스트. 최소 1개를 선택해야 통과합니다.
        본문/변수/모델별 variant 정보는 system 메시지에 자동 주입됩니다.
      </p>
    </div>
  );
}

function SkillCallConfigEditor({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const skillsQ = useQuery({
    queryKey: ['admin', 'skills'],
    queryFn: () => api.adminSkillList(),
  });

  const rawIds = config.allowedSkillIds;
  const selectedIds = Array.isArray(rawIds)
    ? (rawIds.filter((v) => typeof v === 'string') as string[])
    : [];
  const selectedSet = new Set(selectedIds);

  const toggle = (id: string) => {
    const next = selectedSet.has(id) ? selectedIds.filter((v) => v !== id) : [...selectedIds, id];
    onChange({ ...config, allowedSkillIds: next });
  };

  // 위임 대상은 enabled skill 만. 자기 자신 선택은 런타임에서 순환 차단되므로 굳이 제외하지 않는다.
  const skills = (skillsQ.data?.skills ?? []).filter((s) => s.enabled);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs font-medium">호출 가능 Skill</span>
        <span className="text-[11px] text-foreground-muted">
          {selectedIds.length === 0 ? '최소 1개 선택' : `${selectedIds.length}개 선택`}
        </span>
      </div>
      {skillsQ.isLoading ? (
        <p className="text-xs text-foreground-muted">Skill 목록 로딩 중…</p>
      ) : skills.length > 0 ? (
        <div className="space-y-1 rounded-md border border-border bg-surface px-2 py-1.5 max-h-44 overflow-auto">
          {skills.map((s) => (
            <label
              key={s.id}
              className="flex items-center gap-2 text-xs py-1 px-1 rounded hover:bg-surface-muted/40 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(s.id)}
                onChange={() => toggle(s.id)}
                className="size-3.5 accent-accent"
              />
              <span className="font-medium">{s.name}</span>
              <span className="font-mono text-[10px] text-foreground-muted">{s.slug}</span>
            </label>
          ))}
        </div>
      ) : (
        <p className="text-xs text-foreground-muted">활성화된 Skill 이 없습니다.</p>
      )}
      <p className="mt-1 text-[11px] text-foreground-muted">
        이 라우터가 위임할 수 있는 sub-skill 화이트리스트. 최소 1개를 선택해야 호출 시 통과합니다.
        호출한 사용자가 해당 skill 에 접근 권한이 있어야 위임됩니다.
      </p>
    </div>
  );
}

function UserGetAttributesEditor({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const allowedKeys = Array.isArray(config.allowedKeys)
    ? (config.allowedKeys.filter((v) => typeof v === 'string') as string[])
    : [];

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs font-medium">노출 허용 attribute key</span>
        <span className="text-[11px] text-foreground-muted">
          {allowedKeys.length === 0 ? '비우면 전체 활성 정의' : `${allowedKeys.length}개`}
        </span>
      </div>
      <DomainChipInput
        values={allowedKeys}
        onChange={(next) => onChange({ ...config, allowedKeys: next })}
        placeholder="팀명"
      />
      <p className="mt-1 text-[11px] text-foreground-muted">
        스킬마다 노출할 user_attribute_definitions.key 화이트리스트. /admin/permissions/attributes
        에 정의된 키만 의미 있습니다. 비우면 활성 정의 전부 노출됩니다.
      </p>
    </div>
  );
}

function HttpToolConfigEditor({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const endpointsQ = useQuery({
    queryKey: ['admin', 'http-endpoints'],
    queryFn: () => api.adminHttpEndpointList(),
  });

  const rawIds = config.endpointIds;
  const selectedIds = Array.isArray(rawIds)
    ? (rawIds.filter((v) => typeof v === 'string') as string[])
    : [];
  const selectedSet = new Set(selectedIds);

  const toggle = (id: string) => {
    const next = selectedSet.has(id) ? selectedIds.filter((v) => v !== id) : [...selectedIds, id];
    onChange({ ...config, endpointIds: next });
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs font-medium">허용 Endpoint</span>
        <span className="text-[11px] text-foreground-muted">
          {selectedIds.length === 0 ? '선택 필요' : `${selectedIds.length}개 선택`}
        </span>
      </div>
      {endpointsQ.isLoading ? (
        <p className="text-xs text-foreground-muted">Endpoint 목록 로딩 중…</p>
      ) : endpointsQ.data && endpointsQ.data.endpoints.length > 0 ? (
        <div className="space-y-1 rounded-md border border-border bg-surface px-2 py-1.5 max-h-44 overflow-auto">
          {endpointsQ.data.endpoints.map((ep) => (
            <label
              key={ep.id}
              className="flex items-center gap-2 text-xs py-1 px-1 rounded hover:bg-surface-muted/40 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(ep.id)}
                onChange={() => toggle(ep.id)}
                className="size-3.5 accent-accent"
              />
              <span className="font-medium">{ep.name}</span>
              <span className="font-mono text-[10px] text-foreground-muted">
                {ep.baseUrl} · {ep.authType}
              </span>
            </label>
          ))}
        </div>
      ) : (
        <p className="text-xs text-foreground-muted">
          등록된 Endpoint 가 없습니다. 먼저 /admin/http-endpoints 에서 추가하세요.
        </p>
      )}
    </div>
  );
}

function DbConfigEditor({
  config,
  onChange,
  withMaxRows,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  withMaxRows: boolean;
}) {
  const dataSourcesQ = useQuery({
    queryKey: ['admin', 'data-sources'],
    queryFn: () => api.dataSourceList(),
  });

  const rawIds = config.dataSourceIds;
  const selectedIds = Array.isArray(rawIds)
    ? (rawIds.filter((v) => typeof v === 'string') as string[])
    : [];
  const selectedSet = new Set(selectedIds);

  const toggle = (id: string) => {
    const next = selectedSet.has(id) ? selectedIds.filter((v) => v !== id) : [...selectedIds, id];
    onChange({ ...config, dataSourceIds: next });
  };

  const maxRowsRaw = config.maxRows;
  const _maxRows = typeof maxRowsRaw === 'number' ? maxRowsRaw : 200;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs font-medium">허용 데이터소스</span>
          <span className="text-[11px] text-foreground-muted">
            {selectedIds.length === 0 ? '비우면 전체 허용' : `${selectedIds.length}개 선택`}
          </span>
        </div>
        {dataSourcesQ.isLoading ? (
          <p className="text-xs text-foreground-muted">데이터소스 목록 로딩 중…</p>
        ) : dataSourcesQ.data && dataSourcesQ.data.dataSources.length > 0 ? (
          <div className="space-y-1 rounded-md border border-border bg-surface px-2 py-1.5 max-h-44 overflow-auto">
            {dataSourcesQ.data.dataSources.map((ds) => (
              <label
                key={ds.id}
                className="flex items-center gap-2 text-xs py-1 px-1 rounded hover:bg-surface-muted/40 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(ds.id)}
                  onChange={() => toggle(ds.id)}
                  className="size-3.5 accent-accent"
                />
                <span className="font-medium">{ds.name}</span>
                <span className="font-mono text-[10px] text-foreground-muted">
                  {ds.kind} · {ds.host}:{ds.port}/{ds.database}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-xs text-foreground-muted">등록된 데이터소스가 없습니다.</p>
        )}
      </div>

      {withMaxRows ? (
        <MaxRowsEditor config={config} onChange={onChange} defaultValue={200} min={1} max={1000} />
      ) : null}
    </div>
  );
}

function MaxRowsEditor({
  config,
  onChange,
  defaultValue,
  min,
  max,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  defaultValue: number;
  min: number;
  max: number;
}) {
  const raw = config.maxRows;
  const value = typeof raw === 'number' ? raw : defaultValue;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-xs font-medium" htmlFor="tool-max-rows">
          최대 반환 행 수
        </label>
        <span className="text-[11px] text-foreground-muted">
          {min}–{max} (기본 {defaultValue})
        </span>
      </div>
      <input
        id="tool-max-rows"
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) {
            onChange({ ...config, maxRows: n });
          }
        }}
        className="h-9 w-32 rounded-md border border-border bg-surface px-3 text-sm"
      />
    </div>
  );
}

function WebFetchUrlEditor({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const allowList = Array.isArray(config.allowList)
    ? (config.allowList.filter((v) => typeof v === 'string') as string[])
    : [];
  const denyList = Array.isArray(config.denyList)
    ? (config.denyList.filter((v) => typeof v === 'string') as string[])
    : [];
  const maxCharsRaw = config.maxChars;
  const maxChars = typeof maxCharsRaw === 'number' ? maxCharsRaw : 4000;
  const defaultExtractRaw = config.defaultExtract;
  const defaultExtract =
    defaultExtractRaw === 'markdown' || defaultExtractRaw === 'html' ? defaultExtractRaw : 'text';

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs font-medium">허용 도메인 (allowList)</span>
          <span className="text-[11px] text-foreground-muted">
            {allowList.length === 0 ? '비우면 공개 웹 전체' : `${allowList.length}개`}
          </span>
        </div>
        <DomainChipInput
          values={allowList}
          onChange={(next) => onChange({ ...config, allowList: next })}
          placeholder="example.com"
        />
        <p className="mt-1 text-[11px] text-foreground-muted">
          suffix 매칭. 사설/내부 IP 는 언제나 차단됩니다.
        </p>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs font-medium">차단 도메인 (denyList)</span>
          <span className="text-[11px] text-foreground-muted">{denyList.length}개</span>
        </div>
        <DomainChipInput
          values={denyList}
          onChange={(next) => onChange({ ...config, denyList: next })}
          placeholder="bad.example.com"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label className="text-xs font-medium" htmlFor="tool-max-chars">
              본문 최대 글자수
            </label>
            <span className="text-[11px] text-foreground-muted">100–20000</span>
          </div>
          <input
            id="tool-max-chars"
            type="number"
            min={100}
            max={20000}
            value={maxChars}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) {
                onChange({ ...config, maxChars: n });
              }
            }}
            className="h-9 w-32 rounded-md border border-border bg-surface px-3 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" htmlFor="tool-extract">
            기본 추출 모드
          </label>
          <select
            id="tool-extract"
            value={defaultExtract}
            onChange={(e) => onChange({ ...config, defaultExtract: e.target.value })}
            className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            <option value="text">text (본문 텍스트)</option>
            <option value="markdown">markdown</option>
            <option value="html">html (sanitized)</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function WebSearchGroundedEditor({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const llmQ = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
  });

  const endpointId = typeof config.endpointId === 'string' ? config.endpointId : '';
  const model = typeof config.model === 'string' ? config.model : '';
  const allowDomains = Array.isArray(config.allowDomains)
    ? (config.allowDomains.filter((v) => typeof v === 'string') as string[])
    : [];
  const denyDomains = Array.isArray(config.denyDomains)
    ? (config.denyDomains.filter((v) => typeof v === 'string') as string[])
    : [];
  const maxCallsPerTurn = typeof config.maxCallsPerTurn === 'number' ? config.maxCallsPerTurn : 3;
  const monthlyBudgetRaw = config.monthlyBudget;
  const monthlyBudget = typeof monthlyBudgetRaw === 'number' ? monthlyBudgetRaw : null;
  const requireSources = typeof config.requireSources === 'boolean' ? config.requireSources : true;

  // Gemini API + GCP Vertex AI 둘 다 grounded search 지원 — 같은 @google/genai SDK + googleSearch tool.
  const groundedEndpoints =
    llmQ.data?.endpoints.filter((ep) => ep.provider === 'gemini' || ep.provider === 'gcp') ?? [];

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="text-xs font-medium" htmlFor="grounded-endpoint">
            Grounding Endpoint
          </label>
          <span className="text-[11px] text-foreground-muted">
            provider=gemini 또는 gcp (Vertex AI) 사용 가능
          </span>
        </div>
        {llmQ.isLoading ? (
          <p className="text-xs text-foreground-muted">Endpoint 목록 로딩 중…</p>
        ) : groundedEndpoints.length === 0 ? (
          <p className="text-xs text-foreground-muted">
            등록된 grounding 호환 endpoint (gemini / gcp) 가 없습니다. 먼저 /admin/llm-endpoints
            에서 추가하세요.
          </p>
        ) : (
          <select
            id="grounded-endpoint"
            value={endpointId}
            onChange={(e) => onChange({ ...config, endpointId: e.target.value })}
            className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            <option value="">선택 안 함</option>
            {groundedEndpoints.map((ep) => (
              <option key={ep.id} value={ep.id}>
                {ep.displayName ?? ep.name} · {ep.model} ({ep.provider})
              </option>
            ))}
          </select>
        )}
      </div>

      <div>
        <label className="text-xs font-medium block mb-1.5" htmlFor="grounded-model">
          모델 override{' '}
          <span className="text-foreground-muted">(빈 값이면 endpoint 의 model 사용)</span>
        </label>
        <input
          id="grounded-model"
          type="text"
          value={model}
          onChange={(e) => {
            const v = e.target.value.trim();
            const next = { ...config };
            if (v) next.model = v;
            else next.model = undefined;
            onChange(next);
          }}
          placeholder="gemini-2.5-flash"
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm font-mono"
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs font-medium">허용 도메인 (allowDomains)</span>
          <span className="text-[11px] text-foreground-muted">
            {allowDomains.length === 0 ? '비우면 공개 웹 전체' : `${allowDomains.length}개`}
          </span>
        </div>
        <DomainChipInput
          values={allowDomains}
          onChange={(next) => onChange({ ...config, allowDomains: next })}
          placeholder="example.com"
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs font-medium">차단 도메인 (denyDomains)</span>
          <span className="text-[11px] text-foreground-muted">{denyDomains.length}개</span>
        </div>
        <DomainChipInput
          values={denyDomains}
          onChange={(next) => onChange({ ...config, denyDomains: next })}
          placeholder="bad.example.com"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label className="text-xs font-medium" htmlFor="grounded-mct">
              한 turn 최대 호출
            </label>
            <span className="text-[11px] text-foreground-muted">1–10 (기본 3)</span>
          </div>
          <input
            id="grounded-mct"
            type="number"
            min={1}
            max={10}
            value={maxCallsPerTurn}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) onChange({ ...config, maxCallsPerTurn: n });
            }}
            className="h-9 w-32 rounded-md border border-border bg-surface px-3 text-sm"
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label className="text-xs font-medium" htmlFor="grounded-budget">
              월간 호출 budget
            </label>
            <span className="text-[11px] text-foreground-muted">비우면 무제한</span>
          </div>
          <input
            id="grounded-budget"
            type="number"
            min={1}
            value={monthlyBudget ?? ''}
            onChange={(e) => {
              const v = e.target.value.trim();
              const next = { ...config };
              if (v === '') {
                next.monthlyBudget = null;
              } else {
                const n = Number(v);
                if (Number.isFinite(n) && n >= 1) next.monthlyBudget = n;
              }
              onChange(next);
            }}
            className="h-9 w-32 rounded-md border border-border bg-surface px-3 text-sm"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={requireSources}
          onChange={(e) => onChange({ ...config, requireSources: e.target.checked })}
          className="size-3.5 accent-accent"
        />
        <span>응답에 출처 표시 강제 (필터 후 0개 이면 ok:false)</span>
      </label>

      <p className="text-[11px] text-foreground-muted">
        Gemini Grounding API 호출당 비용이 발생합니다. 안전상 가능하면 도메인 화이트리스트와 월간
        budget 을 함께 설정하세요.
      </p>
    </div>
  );
}

function DomainChipInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');

  const push = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (values.includes(v)) {
      setDraft('');
      return;
    }
    onChange([...values, v]);
    setDraft('');
  };

  const remove = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      push();
    } else if (e.key === 'Backspace' && draft.length === 0 && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap gap-1.5 items-center rounded-md border border-border bg-surface px-2 py-1.5 min-h-9',
      )}
    >
      {values.map((v, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: 같은 도메인 문자열이 중복될 수 있어 index 를 함께 사용
          key={`${v}-${i}`}
          className="inline-flex items-center gap-1 rounded-sm bg-surface-muted px-2 h-6 text-xs font-mono"
        >
          {v}
          <button
            type="button"
            onClick={() => remove(i)}
            aria-label={`${v} 제거`}
            className="text-foreground-muted hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={push}
        placeholder={values.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[80px] bg-transparent text-sm focus-visible:outline-none"
      />
    </div>
  );
}

/**
 * analysis.* 툴 공용 config 편집기.
 * - 모든 analysis 툴: endpointId + path
 * - verify_tag 전용: logPath (어드민 채팅 로그의 "에이전트 로그 보기" 가 이 path 로 조회)
 *   → logPathDefault prop 이 주어졌을 때만 입력 필드 노출
 */
function AnalysisToolEditor({
  config,
  onChange,
  defaultPath,
  logPathDefault,
  withSearchYn,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  defaultPath: string;
  logPathDefault?: string;
  /** verify_tag 전용 — Insight Agent 카테고리 분기 (search_yn=Y|N) 토글. */
  withSearchYn?: boolean;
}) {
  const endpointsQ = useQuery({
    queryKey: ['admin', 'http-endpoints'],
    queryFn: () => api.adminHttpEndpointList(),
  });

  // 같은 페이지에 여러 위젯이 동시에 떠 있어도 label htmlFor / input id 충돌이 없도록
  // React 의 useId() 로 고유 prefix 생성. 정적 id ("analysis-tool-endpoint") 는 멀티-instance
  // 환경에서 label 클릭 시 항상 첫 위젯에 focus 가 가는 사고를 일으킨다.
  const idPrefix = useId();
  const endpointInputId = `${idPrefix}-endpoint`;
  const pathInputId = `${idPrefix}-path`;
  const logPathInputId = `${idPrefix}-log-path`;

  const endpointId = typeof config.endpointId === 'string' ? config.endpointId : '';
  const path = typeof config.path === 'string' ? config.path : '';
  const logPath = typeof config.logPath === 'string' ? config.logPath : '';
  const searchYn = config.searchYn === 'Y' ? 'Y' : 'N';

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="text-xs font-medium" htmlFor={endpointInputId}>
            Insight Agent Endpoint
          </label>
          <span className="text-[11px] text-foreground-muted">http_endpoints 에서 선택</span>
        </div>
        {endpointsQ.isLoading ? (
          <p className="text-xs text-foreground-muted">Endpoint 목록 로딩 중…</p>
        ) : endpointsQ.data && endpointsQ.data.endpoints.length > 0 ? (
          <select
            id={endpointInputId}
            value={endpointId}
            onChange={(e) => onChange({ ...config, endpointId: e.target.value })}
            className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            <option value="">선택 안 함</option>
            {endpointsQ.data.endpoints.map((ep) => (
              <option key={ep.id} value={ep.id}>
                {ep.name}
                {ep.description ? ` · ${ep.description}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-xs text-foreground-muted">
            등록된 HTTP endpoint 가 없습니다. 먼저 /admin/http-endpoints 에서 추가하세요.
          </p>
        )}
      </div>

      <div>
        <label className="text-xs font-medium block mb-1.5" htmlFor={pathInputId}>
          Path <span className="text-foreground-muted">(빈 값이면 기본 {defaultPath})</span>
        </label>
        <input
          id={pathInputId}
          type="text"
          value={path}
          onChange={(e) => {
            const v = e.target.value;
            const next = { ...config };
            if (v.trim()) next.path = v;
            else next.path = undefined;
            onChange(next);
          }}
          placeholder={defaultPath}
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm font-mono"
        />
      </div>

      {logPathDefault ? (
        <div>
          <label className="text-xs font-medium block mb-1.5" htmlFor={logPathInputId}>
            Log Path{' '}
            <span className="text-foreground-muted">(빈 값이면 기본 {logPathDefault})</span>
          </label>
          <input
            id={logPathInputId}
            type="text"
            value={logPath}
            onChange={(e) => {
              const v = e.target.value;
              const next = { ...config };
              if (v.trim()) next.logPath = v;
              else next.logPath = undefined;
              onChange(next);
            }}
            placeholder={logPathDefault}
            className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm font-mono"
          />
          <p className="text-[11px] text-foreground-muted mt-1">
            어드민 채팅 로그 — "에이전트 로그 보기" 버튼이 이 path 로 외부 로그를 조회합니다.
          </p>
        </div>
      ) : null}

      {withSearchYn ? (
        <div>
          <div className="text-xs font-medium mb-1.5">Insight Agent 카테고리</div>
          <div className="flex gap-2">
            {(['N', 'Y'] as const).map((v) => (
              <label
                key={v}
                className={cn(
                  'flex-1 cursor-pointer rounded-md border px-3 py-2 text-xs',
                  searchYn === v
                    ? 'border-accent bg-accent/10 font-medium'
                    : 'border-border bg-surface',
                )}
              >
                <input
                  type="radio"
                  className="hidden"
                  checked={searchYn === v}
                  onChange={() => onChange({ ...config, searchYn: v })}
                />
                {v === 'N' ? '현황분석 (search_yn=N)' : '데이터탐색 (search_yn=Y)'}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-foreground-muted mt-1">
            현황분석은 apply_task / get_data 후속, 데이터탐색은 datasearch.* 후속과 함께 묶어
            활성화하세요.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * datasearch.* 5개 툴 공용 admin config 위젯.
 * - 모든 툴: endpointId + path + promptId + cacheYn
 * - split 전용: tag (chip list — 분리할 태그 배열). withTags prop 로 노출.
 *
 * 운영자가 endpointId / promptId 를 비워두면 nonNilUuid / min(1) 가드가 호출 시점에 차단한다.
 */
function DatasearchToolEditor({
  config,
  onChange,
  defaultPath,
  withTags,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  defaultPath: string;
  /** datasearch.split 전용 — 분리할 태그 chip 입력 표시. */
  withTags?: boolean;
}) {
  const endpointsQ = useQuery({
    queryKey: ['admin', 'http-endpoints'],
    queryFn: () => api.adminHttpEndpointList(),
  });

  // useId() 로 위젯 인스턴스별 고유 prefix — 같은 페이지의 5개 datasearch 위젯이 label/id 충돌 없이 동작.
  const idPrefix = useId();
  const endpointInputId = `${idPrefix}-endpoint`;
  const pathInputId = `${idPrefix}-path`;
  const promptIdInputId = `${idPrefix}-prompt-id`;
  const tagInputId = `${idPrefix}-tag`;

  const endpointId = typeof config.endpointId === 'string' ? config.endpointId : '';
  const path = typeof config.path === 'string' ? config.path : '';
  const promptId = typeof config.promptId === 'string' ? config.promptId : '';
  const cacheYn = config.cacheYn === 'Y' ? 'Y' : 'N';
  const tagList = Array.isArray(config.tag)
    ? (config.tag.filter((v) => typeof v === 'string') as string[])
    : [];
  const [tagInput, setTagInput] = useState('');

  const commitTag = () => {
    const v = tagInput.trim();
    if (!v) return;
    if (tagList.includes(v)) {
      setTagInput('');
      return;
    }
    onChange({ ...config, tag: [...tagList, v] });
    setTagInput('');
  };

  const onTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag();
    } else if (e.key === 'Backspace' && tagInput === '' && tagList.length > 0) {
      onChange({ ...config, tag: tagList.slice(0, -1) });
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="text-xs font-medium" htmlFor={endpointInputId}>
            Insight Agent Endpoint
          </label>
          <span className="text-[11px] text-foreground-muted">http_endpoints 에서 선택</span>
        </div>
        {endpointsQ.isLoading ? (
          <p className="text-xs text-foreground-muted">Endpoint 목록 로딩 중…</p>
        ) : endpointsQ.data && endpointsQ.data.endpoints.length > 0 ? (
          <select
            id={endpointInputId}
            value={endpointId}
            onChange={(e) => onChange({ ...config, endpointId: e.target.value })}
            className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm"
          >
            <option value="">선택 안 함</option>
            {endpointsQ.data.endpoints.map((ep) => (
              <option key={ep.id} value={ep.id}>
                {ep.name}
                {ep.description ? ` · ${ep.description}` : ''}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-xs text-foreground-muted">
            등록된 HTTP endpoint 가 없습니다. 먼저 /admin/http-endpoints 에서 추가하세요.
          </p>
        )}
      </div>

      <div>
        <label className="text-xs font-medium block mb-1.5" htmlFor={pathInputId}>
          Path <span className="text-foreground-muted">(빈 값이면 기본 {defaultPath})</span>
        </label>
        <input
          id={pathInputId}
          type="text"
          value={path}
          onChange={(e) => {
            const v = e.target.value;
            const next = { ...config };
            if (v.trim()) next.path = v;
            else next.path = undefined;
            onChange(next);
          }}
          placeholder={defaultPath}
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm font-mono"
        />
      </div>

      <div>
        <label className="text-xs font-medium block mb-1.5" htmlFor={promptIdInputId}>
          Prompt ID <span className="text-foreground-muted">(필수)</span>
        </label>
        <input
          id={promptIdInputId}
          type="text"
          value={promptId}
          onChange={(e) => onChange({ ...config, promptId: e.target.value })}
          placeholder="Insight Agent 측에 정의된 prompt 식별자"
          className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm font-mono"
        />
      </div>

      <div>
        <div className="text-xs font-medium mb-1.5">Cache</div>
        <div className="flex gap-2">
          {(['N', 'Y'] as const).map((v) => (
            <label
              key={v}
              className={cn(
                'flex-1 cursor-pointer rounded-md border px-3 py-2 text-xs',
                cacheYn === v
                  ? 'border-accent bg-accent/10 font-medium'
                  : 'border-border bg-surface',
              )}
            >
              <input
                type="radio"
                className="hidden"
                checked={cacheYn === v}
                onChange={() => onChange({ ...config, cacheYn: v })}
              />
              cache_yn = {v}
            </label>
          ))}
        </div>
      </div>

      {withTags ? (
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label className="text-xs font-medium" htmlFor={tagInputId}>
              분리 태그 <span className="text-foreground-muted">(필수, Enter / , 로 추가)</span>
            </label>
            <span className="text-[11px] text-foreground-muted">
              {tagList.length === 0 ? '입력 필요' : `${tagList.length}개`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1.5 min-h-9">
            {tagList.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-full bg-surface-muted px-2 py-0.5 text-[11px] font-mono"
              >
                {t}
                <button
                  type="button"
                  className="text-foreground-muted hover:text-foreground"
                  onClick={() => onChange({ ...config, tag: tagList.filter((v) => v !== t) })}
                  aria-label={`태그 ${t} 삭제`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <input
              id={tagInputId}
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={onTagKey}
              onBlur={commitTag}
              placeholder={tagList.length === 0 ? '예: gmvDataSearch' : ''}
              className="flex-1 min-w-20 bg-transparent text-xs font-mono outline-none"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EmailSendEditor({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const allowedDomains = Array.isArray(config.allowedDomains)
    ? (config.allowedDomains.filter((v) => typeof v === 'string') as string[])
    : [];
  const maxRecipients = typeof config.maxRecipients === 'number' ? config.maxRecipients : 20;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs font-medium">허용 도메인 (allowedDomains)</span>
          <span className="text-[11px] text-foreground-muted">
            {allowedDomains.length === 0 ? '비우면 전체 도메인 허용' : `${allowedDomains.length}개`}
          </span>
        </div>
        <DomainChipInput
          values={allowedDomains}
          onChange={(next) => onChange({ ...config, allowedDomains: next })}
          placeholder="sailingstone.io"
        />
        <p className="mt-1 text-[11px] text-foreground-muted">
          suffix 매칭. 지정 시 해당 도메인 수신자만 허용됩니다.
        </p>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <label className="text-xs font-medium" htmlFor="email-max-recipients">
            최대 수신자 수
          </label>
          <span className="text-[11px] text-foreground-muted">1–50 (기본 20)</span>
        </div>
        <input
          id="email-max-recipients"
          type="number"
          min={1}
          max={50}
          value={maxRecipients}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n) && n >= 1 && n <= 50) {
              onChange({ ...config, maxRecipients: n });
            }
          }}
          className="h-9 w-32 rounded-md border border-border bg-surface px-3 text-sm"
        />
        <p className="mt-1 text-[11px] text-foreground-muted">
          한 번의 발송 요청에서 허용되는 최대 수신자 수입니다.
        </p>
      </div>
    </div>
  );
}
